import { Badge } from "@/components/ui/badge";
import type { QualificationForm } from "@/features/qualifications/types";
import type { ColumnDef } from "@tanstack/react-table";
import { Action } from "./columns-action";
import { useDateFormat, useTranslation } from "@/features/utils/i18n";

export const useFormsColumns = (): ColumnDef<QualificationForm>[] => {
  const { t } = useTranslation("qualifications");
  const { format } = useDateFormat();
  return [
    {
      accessorKey: "name",
      header: t("forms.columns.name"),
      cell: ({ row }) => {
        const form = row.original;
        return (
          <div>
            <div className="font-medium">
              {form.name || t("forms.untitled")}
            </div>
            {form.description && (
              <div className="text-sm text-muted-foreground line-clamp-1">
                {form.description}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "status",
      header: t("forms.columns.status"),
      cell: ({ row }) => {
        const status = row.original.status;
        if (!status) return null;

        const variants: Record<string, "default" | "secondary" | "outline"> = {
          active: "default",
          draft: "secondary",
          inactive: "outline",
        };
        return (
          <Badge variant={variants[status] || "secondary"}>
            {t(`forms.status.${status}`, status)}
          </Badge>
        );
      },
    },
    {
      accessorKey: "questions",
      header: t("forms.columns.questions"),
      cell: ({ row }) => {
        const questionCount = row.original.questions?.questions?.length || 0;
        return (
          <span className="text-sm text-muted-foreground">
            {t("forms.columns.questionCount", { count: questionCount })}
          </span>
        );
      },
    },
    {
      accessorKey: "scoring_strategy",
      header: t("forms.columns.scoring"),
      cell: ({ row }) => {
        const strategy = row.original.scoring_strategy;
        if (!strategy) return null;
        return (
          <span className="text-sm">
            {t(`forms.scoring.${strategy}`, strategy)}
          </span>
        );
      },
    },
    {
      accessorKey: "auto_accept_threshold",
      header: t("forms.columns.autoStatus"),
      cell: ({ row }) => {
        const form = row.original;
        if (!form.auto_accept_threshold && !form.auto_reject_threshold) {
          return (
            <span className="text-sm text-muted-foreground">
              {t("forms.columns.manual")}
            </span>
          );
        }
        return (
          <div className="text-xs">
            {form.auto_accept_threshold !== undefined && (
              <div className="text-success">
                {t("forms.columns.accept", {
                  value: form.auto_accept_threshold,
                })}
              </div>
            )}
            {form.auto_reject_threshold !== undefined && (
              <div className="text-danger">
                {t("forms.columns.reject", {
                  value: form.auto_reject_threshold,
                })}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: "created_at",
      header: t("forms.columns.created"),
      cell: ({ row }) => {
        const createdAt = row.original.created_at;
        if (!createdAt) return null;
        return (
          <span className="text-sm text-muted-foreground">
            {format(createdAt, "PP")}
          </span>
        );
      },
    },
    {
      id: "actions",
      header: () => (
        <div className="text-right">{t("forms.columns.actions")}</div>
      ),
      cell: ({ row }) => <Action form={row.original} />,
    },
  ];
};
